import React, { useState, useMemo, useCallback } from "react";
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Page,
  Sort,
} from "@syncfusion/ej2-react-grids";
import {
  Box,
  Chip,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { PortalUserStatusChip } from "./PortalUserStatusChip";
import type { PortalUserView } from "../../../../services/ProfileService/types/portalUserTypes";

interface PortalUsersGridProps {
  users: PortalUserView[];
  isLoading: boolean;
  onRowSelect: (user: PortalUserView) => void;
  onAction: (action: string, user: PortalUserView) => void;
}

type StatusFilter = "All" | "Active" | "Pending" | "Disabled";

export function PortalUsersGrid({ users, isLoading, onRowSelect, onAction }: PortalUsersGridProps) {
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("All");
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => setDebouncedSearch(value), 250);
    setDebounceTimer(timer);
  }, [debounceTimer]);

  const handleStatusFilterChange = (_event: React.MouseEvent<HTMLElement>, newFilter: StatusFilter | null) => {
    if (newFilter !== null) {
      setStatusFilter(newFilter);
    }
  };

  const filteredUsers = useMemo(() => {
    let result = users;

    // Status filter
    if (statusFilter !== "All") {
      result = result.filter((u) => u.status === statusFilter);
    }

    // Search filter (case-insensitive on displayName and mail)
    if (debouncedSearch.trim()) {
      const search = debouncedSearch.toLowerCase();
      result = result.filter(
        (u) =>
          u.displayName.toLowerCase().includes(search) ||
          u.mail.toLowerCase().includes(search)
      );
    }

    return result;
  }, [users, statusFilter, debouncedSearch]);

  const statusTemplate = (props: any) => (
    <PortalUserStatusChip status={props.status} expiresAt={props.expiresAt} />
  );

  const actionsTemplate = (props: any) => {
    const user = props as PortalUserView;
    return (
      <Box sx={{ display: "flex", gap: 1 }}>
        <Chip
          label="Edit"
          size="small"
          color="primary"
          variant="outlined"
          clickable
          onClick={(e) => { e.stopPropagation(); onAction("edit", user); }}
        />
        {user.status === "Pending" && (
          <Chip
            label="Resend"
            size="small"
            color="info"
            variant="outlined"
            clickable
            onClick={(e) => { e.stopPropagation(); onAction("resend", user); }}
          />
        )}
        <Chip
          label="Remove"
          size="small"
          color="error"
          variant="outlined"
          clickable
          onClick={(e) => { e.stopPropagation(); onAction("remove", user); }}
        />
      </Box>
    );
  };

  const dateTemplate = (props: any) => {
    const field = props.column?.field;
    const val = props[field];
    if (!val) return <span>—</span>;
    return <span>{new Date(val).toLocaleDateString()}</span>;
  };

  return (
    <Box>
      {/* Search + Filter Bar */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name or email..."
          value={searchText}
          onChange={handleSearchChange}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={handleStatusFilterChange}
          size="small"
          aria-label="Status filter"
        >
          <ToggleButton value="All">All</ToggleButton>
          <ToggleButton value="Active">Active</ToggleButton>
          <ToggleButton value="Pending">Pending</ToggleButton>
          <ToggleButton value="Disabled">Disabled</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Grid */}
      <GridComponent
        dataSource={filteredUsers}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 20 }}
        rowSelected={(args: any) => onRowSelect(args.data as PortalUserView)}
        height="auto"
      >
        <ColumnsDirective>
          <ColumnDirective field="displayName" headerText="Name" width="180" />
          <ColumnDirective field="mail" headerText="Email" width="220" />
          <ColumnDirective field="status" headerText="Status" width="160" template={statusTemplate} />
          <ColumnDirective field="lastSignInDateTime" headerText="Last Sign-In" width="130" template={dateTemplate} />
          <ColumnDirective field="invitedAt" headerText="Invited" width="110" template={dateTemplate} />
          <ColumnDirective headerText="Actions" width="220" template={actionsTemplate} />
        </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>
    </Box>
  );
}
